import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { punchPassesByTargetIdAtom } from "../../stores";
import { PunchPassSummary } from "../../types";

/**
 * Atom family that gets punch pass summary for a given target id.
 * Keeps aggregation logic out of components.
 * Properly tracks dependencies on punchPassesByTargetIdAtom.
 */
export const getPunchPassSummaryAtom = atomFamily(
  (targetId: string | null | undefined) =>
    atom((get): PunchPassSummary | null => {
      if (!targetId) return null;
      const state = get(punchPassesByTargetIdAtom);
      const entry = state?.byTargetId?.[targetId];
      if (!entry) return null;

      const hasRatePunchPassApplied = Boolean(entry.appliedRatePunchPass);
      const hasTransportationPunchPassApplied = Boolean(
        entry.appliedTransportationPunchPass
      );
      const availableRatePunchPasses = entry.availableRatePunchPasses ?? [];
      const availableTransportationPunchPasses =
        entry.availableTransportationPunchPasses ?? [];
      const invalidPunchPasses = entry.invalidPunchPasses ?? [];

      const validPunchPassCount =
        availableRatePunchPasses.length +
        availableTransportationPunchPasses.length;
      const invalidPunchPassCount = invalidPunchPasses.length;
      const totalPunchPassCount =
        validPunchPassCount +
        invalidPunchPassCount +
        Number(hasRatePunchPassApplied) +
        Number(hasTransportationPunchPassApplied);

      const hasPunchPassApplied =
        hasRatePunchPassApplied || hasTransportationPunchPassApplied;
      const hasValidPunchPass = validPunchPassCount > 0 || hasPunchPassApplied;

      return {
        hasValidPunchPass,
        hasPunchPassApplied,
        hasRatePunchPassApplied,
        hasTransportationPunchPassApplied,
        validPunchPassCount,
        invalidPunchPassCount,
        totalPunchPassCount,
        availableRatePunchPasses,
        availableTransportationPunchPasses,
        invalidPunchPasses,
      };
    })
);
